"use client";

import { useRef, useState, type DragEvent } from "react";

/**
 * Shared drag-to-install behaviour for the Blender extension buttons (add-on
 * cards and the whole-repo button). While the native drag is active the original
 * button stays put and a CCW-tilted clone is handed to setDragImage as the drag
 * ghost — the native snapshot ignores a CSS transform on the dragged element but
 * *does* rasterise one on a child, so the tilt only shows via the off-screen
 * clone. Spread the returned handlers on the wrapper div that contains the
 * draggable <a> (framer-motion swallows native drag events on the motion.a
 * itself, so they're listened for on the wrapper as they bubble up).
 */
export function useBlenderDragInstall() {
  const [isDragging, setIsDragging] = useState(false);
  const dragImageRef = useRef<HTMLDivElement | null>(null);

  const onDragStart = (e: DragEvent<HTMLDivElement>) => {
    setIsDragging(true);
    const button = e.currentTarget.querySelector("a");
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const PAD = 16; // breathing room so the rotated corners aren't clipped
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `position:fixed;top:-1000px;left:-1000px;padding:${PAD}px;pointer-events:none;`;
    const clone = button.cloneNode(true) as HTMLElement;
    clone.style.margin = "0";
    clone.style.transform = "rotate(-15deg)";
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    dragImageRef.current = wrapper;
    e.dataTransfer.setDragImage(wrapper, rect.width / 2 + PAD, rect.height / 2 + PAD);
  };

  const onDragEnd = () => {
    setIsDragging(false);
    dragImageRef.current?.remove();
    dragImageRef.current = null;
  };

  return { isDragging, onDragStart, onDragEnd };
}
